import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Typography, styled } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CardImage from './Card';
import PokerChip from './PokerChip';
import Divider from '@mui/material/Divider';
import PositionIndicator from './PositionIndicator';
import { useTranslation } from 'react-i18next';

const GameContainer = styled(Box)({
    width: '100%',
    backgroundColor: '#1e3f5a',
    borderRadius: '10px',
    padding: '10px',
    margin: '0',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
});

const Table = styled(Box)({
    width: '100%',
    backgroundColor: '#0d7e3e',
    borderRadius: '10px',
    position: 'relative',
    marginBottom: '10px',
    display: 'flex',
    flexDirection: 'column',
    padding: '10px',
    boxSizing: 'border-box',
});

const TableHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '140px',
    marginBottom: '10px',
});

const RoundNameContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
});

const RoundName = styled(Typography)({
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#FFD700',
    textTransform: 'uppercase',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: '8px 12px',
    borderRadius: '5px',
});

const CommunityCards = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
    transformOrigin: 'center center',
    flex: 1,
});

const PotContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
});

const PotLabel = styled(Typography)({
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#FF69B4',
    marginRight: '5px',
});

const PlayerInfo = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
});

const PlayerDetails = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '5px',
    gap: '5px',
});

const PlayerName = styled(Typography)({
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#FFD700',
});

const PlayerBalance = styled(Typography)({
    fontSize: '14px',
    color: '#00FF00',
    fontWeight: 'bold',
});

const PlayerCards = styled(Box)({
    display: 'flex',
    gap: '4px',
});

const PlayerChip = styled(Box)({
    position: 'absolute',
    top: '60px',
    left: '75%',
    color: '#000',
    padding: '4px 8px',
    borderRadius: '4px',
});

const PlayerActionBadge = styled(Box)({
    position: 'absolute',
    top: '-30px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#000',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
});

const Controls = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    gap: '5px',
    flexWrap: 'wrap',
    marginTop: '10px',
});

const TurnButton = styled(Button)({
    minWidth: '30px',
    padding: '2px 5px',
    fontSize: '12px',
});

interface PlayerTurnHistoryItem {
    time: string;
    username: string;
    userId: string;
    roundName: string;
    availableActions: Array<any>;
    pot: number;
    communityCards: string[];
    playerCurrentBets: {
        userId: string;
        amount: number;
        username: string;
    }[];
    playerBalances: {
        userId: string;
        username: string;
        amount: number;
    }[];
    playerCards: {
        userId: string;
        username: string;
        cards: string[];
    }[];
    selectedAction: {
        action: string;
        amount: number;
    };
    dealerId: string;
    smallBlindId: string;
    bigBlindId: string;
}

interface PlayerInfo {
    username: string;
    balance: number;
    bet: number;
    cards: string[];
    folded: boolean;
    hasFoldedInCurrentGame: boolean;
}

interface GameState {
    communityCards: string[];
    players: { [userId: string]: PlayerInfo };
    pot: number;
    dealerId: string;
    smallBlindId: string;
    bigBlindId: string;
}

interface MiniPokerGameDisplayProps {
    playerTurnHistory: PlayerTurnHistoryItem[];
}

const PlayerContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'space-around',
    flexWrap: 'wrap',
    gap: '20px',
});

const Player = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    width: 'calc(33% - 20px)',
    marginBottom: '40px',
});

const MiniPokerGameDisplay: React.FC<MiniPokerGameDisplayProps> = ({ playerTurnHistory }) => {
    const { t } = useTranslation('GameRoom');

    // Add validation at the start
    if (!playerTurnHistory?.length) {
        return null;
    }

    const [currentTurn, setCurrentTurn] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [gameState, setGameState] = useState<GameState>({
        communityCards: [],
        players: {},
        pot: 0,
        dealerId: '',
        smallBlindId: '',
        bigBlindId: '',
    });

    // Add safety check for current turn data
    const currentTurnData = playerTurnHistory[currentTurn];
    if (!currentTurnData) {
        return null;
    }

    useEffect(() => {
        if (isPlaying && currentTurn < playerTurnHistory.length - 1) {
            const timer = setTimeout(() => {
                setCurrentTurn(prev => prev + 1);
            }, 2000);
            return () => clearTimeout(timer);
        } else if (currentTurn === playerTurnHistory.length - 1) {
            setIsPlaying(false);
        }
    }, [isPlaying, currentTurn, playerTurnHistory]);

    useEffect(() => {
        updateGameState(currentTurn);
    }, [currentTurn]);

    const updateGameState = (turn: number) => {
        const action = playerTurnHistory[turn];
        const newState: GameState = {
            communityCards: action.communityCards,
            players: {},
            pot: action.pot,
            dealerId: action.dealerId,
            smallBlindId: action.smallBlindId,
            bigBlindId: action.bigBlindId,
        };

        action.playerBalances.forEach(player => {
            newState.players[player.userId] = {
                username: player.username,
                balance: Number(player.amount.toFixed(2)),
                bet: 0,
                cards: [],
                folded: false,
                hasFoldedInCurrentGame: false,
            };
        });

        action.playerCurrentBets.forEach(bet => {
            if (newState.players[bet.userId]) {
                newState.players[bet.userId].bet = bet.amount;
            }
        });

        action.playerCards.forEach(playerCards => {
            if (newState.players[playerCards.userId]) {
                newState.players[playerCards.userId].cards = playerCards.cards;
            }
        });

        const activePlayer = newState.players[action.userId];
        if (activePlayer) {
            if (action.selectedAction.action === 'fold') {
                activePlayer.hasFoldedInCurrentGame = true;
            } else if (['bet', 'raise', 'call'].includes(action.selectedAction.action)) {
                activePlayer.bet = action.selectedAction.amount;
            }
        }

        setGameState(newState);
    };

    const togglePlay = () => {
        setIsPlaying(!isPlaying);
    };

    const jumpToTurn = (turn: number) => {
        setCurrentTurn(turn);
        setIsPlaying(false);
    };

    return (
        <GameContainer>
            <Table>
                <TableHeader style={{ marginBottom: '40px' }}>
                    <RoundNameContainer>
                        <RoundName>{currentTurnData.roundName || 'Unknown'}</RoundName>
                    </RoundNameContainer>
                    <CommunityCards>
                        {gameState.communityCards.map((card: string, index: number) => (
                            <CardImage key={index} card={card} size={80} />
                        ))}
                    </CommunityCards>
                    <PotContainer>
                        <PotLabel>{t('gameInfo.labels.pot')}:</PotLabel>
                        <PokerChip amount={gameState.pot} currency="$" suffix="" />
                    </PotContainer>
                </TableHeader>
                <Divider sx={{ width: '100%', borderColor: 'white', borderWidth: 2, marginBottom: '40px' }} />
                <PlayerContainer>
                    {Object.entries(gameState.players).map(([userId, player]) => (
                        <Player key={userId}>
                            {userId === playerTurnHistory[currentTurn].userId && (
                                <PlayerActionBadge>
                                    {playerTurnHistory[currentTurn].selectedAction.action.toUpperCase()}
                                    {playerTurnHistory[currentTurn].selectedAction.amount > 0 &&
                                        ` $${playerTurnHistory[currentTurn].selectedAction.amount}`}
                                </PlayerActionBadge>
                            )}
                            <PlayerInfo>
                                <PlayerDetails>
                                    <PositionIndicator
                                        isDealer={userId === gameState.dealerId}
                                        isBigBlind={userId === gameState.bigBlindId}
                                        isSmallBlind={userId === gameState.smallBlindId}
                                    />
                                    <PlayerName>{player.username}</PlayerName>
                                </PlayerDetails>
                                <PlayerBalance>${player.balance.toFixed(2)}</PlayerBalance>
                                <PlayerCards>
                                    {player.cards.map((card: string, index: number) => (
                                        <CardImage key={index} card={card} small />
                                    ))}
                                </PlayerCards>
                            </PlayerInfo>
                            {player.bet > 0 && (
                                <PlayerChip>
                                    <PokerChip amount={player.bet} currency="$" suffix="" />
                                </PlayerChip>
                            )}
                            {player.folded && (
                                <Typography variant="caption" style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                                    Folded
                                </Typography>
                            )}
                        </Player>
                    ))}
                </PlayerContainer>
            </Table>
            <Controls>
                <IconButton onClick={togglePlay} size="small">
                    {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                </IconButton>
                {playerTurnHistory.map((_, index) => (
                    <TurnButton
                        key={index}
                        variant={currentTurn === index ? 'contained' : 'outlined'}
                        onClick={() => jumpToTurn(index)}
                    >
                        {index + 1}
                    </TurnButton>
                ))}
            </Controls>
        </GameContainer>
    );
};

export default MiniPokerGameDisplay;
